import { Injectable, inject } from '@angular/core';
import { SymbolType } from '../../../../../domain/symbol.domain';
import { TradingAccount } from '../../../../../domain/account.domain';
import { PriceType, Strategy } from '../../../domain';
import {
  OrderFormParameters,
  OrderStateParameters,
  EquityFormModel,
  MultilegFormModel,
  OptionFormModel,
  StrategyFormModel
} from '../../domain/order-entry.domain';
import { CreateOrderFormService } from './create-order-form.service';
import { CreateMloOrderFormService } from './create-order-mlo-form.service';
import { CreateOrderOptionFormService } from './create-order-option-form.service';
import { CreateStockOrderFormService } from './create-order-stock-form.service';

@Injectable()
export class CreateStrategyOrderFormService extends CreateOrderFormService<StrategyFormModel | MultilegFormModel> {
  private readonly createStockOrderFormService = inject(CreateStockOrderFormService);
  private readonly createOptionOrderFormService = inject(CreateOrderOptionFormService);
  private readonly createMloOrderFormService = inject(CreateMloOrderFormService);

  /**
   * Initialize object that represent state of UI across multiple steps
   */
  initializeViewModel(selectedAccount?: TradingAccount,
                      orderFormParameters?: OrderFormParameters,
                      strategy?: Strategy): StrategyFormModel | MultilegFormModel {

    const isCopy = orderFormParameters?.orderStateParameters?.orderKey !== undefined;

    switch (strategy) {
      case Strategy.OCO:
        return this.initializeOCO(selectedAccount, orderFormParameters, isCopy);
      case Strategy.OTA:
        return this.initializeOTA(selectedAccount, orderFormParameters, isCopy);
      case Strategy.FTO:
        return this.initializeFTO(selectedAccount, orderFormParameters, isCopy);
      case Strategy.MLO:
        return this.createMloOrderFormService.initializeViewModel(selectedAccount, orderFormParameters);
    }

  }

  initializeOCO(selectedAccount: TradingAccount,
                orderFormParameters: OrderFormParameters, isCopy: boolean): StrategyFormModel {

    const params = isCopy ?
      orderFormParameters :
      { orderStateParameters: { price: PriceType.LIMIT } as OrderStateParameters } as OrderFormParameters;

    const firstItem = this.createStockOrderFormService.initializeViewModel(selectedAccount, params, Strategy.SPL, 0);

    const params2 = isCopy ?
      orderFormParameters :
      { orderStateParameters: { price: PriceType.STOP_LIMIT } as OrderStateParameters } as OrderFormParameters;
    const secondItem = this.createStockOrderFormService.initializeViewModel(selectedAccount, params2, Strategy.SPL, 1);

    return {
      items: [firstItem, secondItem],
      strategyType: Strategy.OCO
    };
  }

  initializeFTO(selectedAccount: TradingAccount,
                orderFormParameters: OrderFormParameters, isCopy: boolean): StrategyFormModel {

    const params = isCopy ?
      orderFormParameters :
      { orderStateParameters: { price: PriceType.LIMIT } as OrderStateParameters } as OrderFormParameters;

    const firstItem = this.createStockOrderFormService.initializeViewModel(selectedAccount, params, Strategy.SPL, 0);
    const secondItem = this.createStockOrderFormService.initializeViewModel(selectedAccount, params, Strategy.SPL, 1);

    if (!isCopy) {
      // adjust default type for new order's 3rd leg
      params.orderStateParameters.price = PriceType.STOP_LIMIT;
    }
    const thirdItem = this.createStockOrderFormService.initializeViewModel(selectedAccount, params, Strategy.SPL, 2);

    return {
      items: [firstItem, secondItem, thirdItem],
      strategyType: Strategy.FTO
    };
  }

  initializeOTA(selectedAccount: TradingAccount,
                orderFormParameters: OrderFormParameters, isCopy: boolean): StrategyFormModel {

    const params = isCopy ?
      orderFormParameters :
      { orderStateParameters: { price: PriceType.LIMIT } as OrderStateParameters } as OrderFormParameters;

    const firstType = isCopy ?
      orderFormParameters.orderDetails.orderTerms.order[0].security.type :
      SymbolType.EQUITY;
    const secondType = isCopy ?
      orderFormParameters.orderDetails.orderTerms.order[1].security.type :
      SymbolType.EQUITY;

    const firstItem = this.initializeItem(selectedAccount, params, firstType, 0);
    const secondItem = this.initializeItem(selectedAccount, params, secondType, 1);

    return {
      items: [firstItem, secondItem],
      strategyType: Strategy.OTA
    };
  }

  initializeItem(selectedAccount: TradingAccount,
                 orderFormParameters: OrderFormParameters,
                 type: SymbolType,
                 item?: number): EquityFormModel | OptionFormModel {

    return type === SymbolType.OPTION ?
      this.createOptionOrderFormService.initializeViewModel(selectedAccount, orderFormParameters, Strategy.SPL, item) :
      this.createStockOrderFormService.initializeViewModel(selectedAccount, orderFormParameters, Strategy.SPL, item);
  }

  getParamsOrDefault(isCopy: boolean, defaultParams?: OrderFormParameters): OrderFormParameters {
    return isCopy ?
      defaultParams :
      { orderStateParameters: { price: PriceType.LIMIT } as OrderStateParameters } as OrderFormParameters;
  }
}

